import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveAddressbookCollection } from "../../graphQL/addressbook";

export const useAddressbookCollection = ({
  authorId,
  collectionKey,
  pagination,
  ordering,
  filters,
  allowWithoutAuthorId = false,
  extraFetchParams,
  loadingTimeoutMs = 12000,
} = {}) => {
  const collection = useMemo(
    () => resolveAddressbookCollection(collectionKey),
    [collectionKey]
  );

  const [state, setState] = useState({
    loading: false,
    error: "",
    total: 0,
    items: [],
  });

  const load = useCallback(async () => {
    if (!authorId && !allowWithoutAuthorId) {
      setState({ loading: false, error: "", total: 0, items: [] });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: "" }));
    let completed = false;
    const timeoutId = setTimeout(() => {
      if (completed) return;
      // Prevent indefinite spinner if the backend hangs.
      // We intentionally keep error empty so the existing empty-state message renders.
      setState((prev) => ({ ...prev, loading: false, error: "" }));
    }, loadingTimeoutMs);

    try {
      const result = await collection.fetch({
        authorId,
        pagination: pagination || collection.defaultPagination,
        ordering: ordering || collection.defaultOrdering,
        filters: filters || {},
        ...(extraFetchParams || {}),
      });

      completed = true;
      clearTimeout(timeoutId);
      setState({
        loading: false,
        error: "",
        total: result?.total || 0,
        items: result?.items || [],
      });
    } catch (error) {
      completed = true;
      clearTimeout(timeoutId);
      setState({
        loading: false,
        error: error?.message || "Failed to load collection",
        total: 0,
        items: [],
      });
    }
  }, [authorId, allowWithoutAuthorId, collection, pagination, ordering, filters, extraFetchParams, loadingTimeoutMs]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      await load();
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [load]);

  const patchItem = useCallback((id, patch) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.map((it) => (it?.id === id ? { ...it, ...patch } : it)),
    }));
  }, []);

  const removeItem = useCallback((id) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.filter((it) => it?.id !== id),
      total: Math.max(0, (prev.total || 0) - 1),
    }));
  }, []);

  return {
    collection,
    loading: state.loading,
    error: state.error,
    total: state.total,
    items: state.items,
    reload: load,
    patchItem,
    removeItem,
  };
};
